import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useAppDispatch, useAppSelector } from '../../../helpers/hooks';
import { flattenKeyFile } from '../../../helpers/keyFile';
import { useWorkspaceSlice } from '../slice';
import { selectImporting } from '../slice/selectors';
import { ImportKeyItem } from '../slice/types';

interface ImportKeysDialogProps {
  open: boolean;
  projectId: string;
  onClose: () => void;
}

export default function ImportKeysDialog({
  open,
  projectId,
  onClose,
}: ImportKeysDialogProps) {
  const dispatch = useAppDispatch();
  const { actions } = useWorkspaceSlice();
  const importing = useAppSelector(selectImporting);
  const inputRef = useRef<HTMLInputElement>(null);

  const [fileName, setFileName] = useState('');
  const [entries, setEntries] = useState<ImportKeyItem[]>([]);
  const [prune, setPrune] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setFileName('');
    setEntries([]);
    setPrune(false);
    setError('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    try {
      const parsed = JSON.parse(await file.text());
      const flat = flattenKeyFile(parsed);
      if (flat.length === 0) {
        setError('No translatable string entries were found in this file.');
        setEntries([]);
        return;
      }
      setError('');
      setEntries(flat);
    } catch {
      setError('That file is not valid JSON.');
      setEntries([]);
    }
  };

  const handleImport = () => {
    dispatch(actions.importKeysRequest({ projectId, keys: entries, prune }));
    reset();
    onClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import keys from JSON</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Upload a flat or nested JSON file of source strings. Only new keys are
          added — entries that already exist with the same source text are
          skipped, and existing keys whose source text changed are updated.
        </DialogContentText>

        <Stack spacing={2}>
          <Box>
            <input
              ref={inputRef}
              type="file"
              accept=".json,application/json"
              hidden
              onChange={handleFile}
            />
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => inputRef.current?.click()}
            >
              {fileName || 'Choose JSON file'}
            </Button>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}

          {entries.length > 0 && (
            <Alert severity="info">
              <Typography variant="body2">
                Found <strong>{entries.length}</strong> entries. Example:{' '}
                <code>{entries[0].key}</code> →{' '}
                {entries[0].sourceText.slice(0, 40)}
                {entries[0].sourceText.length > 40 ? '…' : ''}
              </Typography>
            </Alert>
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={prune}
                onChange={(e) => setPrune(e.target.checked)}
              />
            }
            label="Sync mode: remove keys not present in this file"
          />

          {prune && (
            <Alert severity="warning">
              <Typography variant="body2">
                Any key in this project that is not in the uploaded file will be
                permanently deleted, along with its translations and history.
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={entries.length === 0 || importing}
        >
          Import {entries.length > 0 ? `${entries.length} keys` : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
